import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ChevronRight, Users, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, type SpikeTopItem } from '../../services/api';

const HOURS_OPTIONS = [6, 12, 24] as const;
const LIMIT = 9;

// Один запрос на (hours): при Strict Mode второй mount подписывается на тот же promise
let spikesInFlight: Promise<{ spikes: SpikeTopItem[] }> | null = null;
let spikesInFlightHours: number | null = null;

const spikeLevelVariant = (level: string) => {
  switch (level) {
    case 'VIRAL': return 'viral';
    case 'HOT': return 'hot';
    case 'WATCH': return 'watch';
    default: return 'default';
  }
};

const SpikeTopTickers: React.FC = () => {
  const { isPro } = useAuth();
  const [items, setItems] = useState<SpikeTopItem[]>([]);
  const [hours, setHours] = useState<number>(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isPro) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const runFetch = () =>
      apiService.getSpikesTopTickers(hours, LIMIT).finally(() => {
        if (spikesInFlightHours === hours) {
          spikesInFlight = null;
          spikesInFlightHours = null;
        }
      });

    const promise =
      spikesInFlight && spikesInFlightHours === hours
        ? spikesInFlight
        : ((spikesInFlightHours = hours), (spikesInFlight = runFetch()));

    promise
      .then((res) => {
        if (!cancelled) setItems(res.spikes || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isPro, hours]);

  if (!isPro) return null;

  return (
    <div
      className="spike-top-tickers-wrap rounded-2xl p-6 mb-6 overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-400" />
          Growing interest (by spike)
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-white/60">Last</span>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
          >
            {HOURS_OPTIONS.map((h) => (
              <option key={h} value={h} className="bg-[#1a1a1a] text-white">{h}h</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400 mb-4 py-2">{error}</div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-8 text-white/60">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-gold border-t-transparent" />
          <span className="ml-2">Loading…</span>
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-white/50 py-4">No spikes in this window.</p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, index) => {
            const variant = spikeLevelVariant(item.level);
            const topReason = item.reasons && item.reasons.length > 0 ? item.reasons[0] : null;
            return (
              <Link
                key={`${item.ticker}-${item.window_start || ''}`}
                to={`/ticker/${item.ticker}`}
                className={`spike-ticker-card spike-ticker-card--${variant} group block relative overflow-hidden rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg`}
              >
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative p-5">
                  {/* Header: rank + ticker + level */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={`spike-ticker-rank spike-ticker-rank--${variant} flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm shrink-0`}
                      >
                        {index + 1}
                      </span>
                      <span className="font-bold text-xl text-white group-hover:text-brand-gold transition-colors">
                        ${item.ticker}
                      </span>
                    </div>
                    <span
                      className={`spike-ticker-level spike-ticker-level--${variant} text-xs font-bold uppercase px-2.5 py-1 rounded-lg shrink-0 flex items-center gap-1`}
                    >
                      <Flame className="w-3 h-3" />
                      {item.level}
                    </span>
                  </div>

                  {/* Score + metrics */}
                  <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
                    <span className="text-white/80">
                      Score <span className="font-semibold text-brand-gold">{item.score}</span>
                    </span>
                    {item.window_metrics && (
                      <span className="flex items-center gap-2 text-white/50">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {item.window_metrics.mentions} mentions
                        <Users className="w-3.5 h-3.5 ml-1" />
                        {item.window_metrics.unique_users} users
                      </span>
                    )}
                  </div>

                  {/* Top reason */}
                  {topReason && (
                    <p className="text-xs text-white/50 line-clamp-2 mb-3">{topReason}</p>
                  )}

                  {/* CTA */}
                  <div className={`spike-ticker-cta spike-ticker-cta--${variant} flex items-center gap-1 text-xs font-medium`}>
                    <span>View details</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpikeTopTickers;
